import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import { EditOutlined } from '@ant-design/icons';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

const { Text } = Typography;

interface TitleFieldProps {
  field: any;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  commonStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  error?: string;
  formData?: any;
}

export const TitleField: React.FC<TitleFieldProps> = ({
  field,
  value,
  onChange,
  onBlur,
  commonStyle,
  labelStyle,
  error,
  formData
}) => {
  const { fontSize, spacing } = useResponsiveSizes();
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || '');
  const [isCustom, setIsCustom] = useState(false);

  const generateDefaultTitle = React.useCallback(() => {
    const date = dayjs();
    return `Proyecto ${date.format('DD-MM-YYYY')}`;
  }, []);

  useEffect(() => {
    if (!value && !isCustom) {
      const defaultTitle = generateDefaultTitle();
      onChange(defaultTitle);
      setTempValue(defaultTitle);
    }
  }, [formData?.date, generateDefaultTitle, isCustom, onChange, value]);

  const hoverStyle = `
  .title-field-container:hover .edit-icon {
    opacity: 1 !important;
  }
`;

  const textStyle: React.CSSProperties = {
    fontSize: fontSize.xl,
    fontWeight: '600',
    padding: `${spacing.sm}px ${spacing.sm}px ${spacing.sm}px 0`
  };

  return (
    <>
      <style>{hoverStyle}</style>
      <div className='title-field-container' style={commonStyle}>
        <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
          {field.label}
        </Text>

        {isEditing ? (
          <input
            autoFocus
            value={tempValue}
            onChange={e => setTempValue(e.target.value)}
            onBlur={() => {
              onChange(tempValue);
              setIsCustom(true);
              setIsEditing(false);
              onBlur?.();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onChange(tempValue);
                setIsCustom(true);
                setIsEditing(false);
              }
            }}
            style={{
              ...textStyle,
              border: 'none',
              borderBottom: '2px solid #2872b7',
              outline: 'none',
              width: '100%',
              background: 'transparent',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              boxSizing: 'border-box',
              marginBottom: '-2px'
            }}
          />
        ) : (
          <div
            onClick={() => setIsEditing(true)}
            style={{
              ...textStyle,
              cursor: 'text',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              position: 'relative'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {value || generateDefaultTitle()}

            <EditOutlined
              style={{
                fontSize: fontSize.sm,
                color: '#999',
                opacity: 0,
                transition: 'opacity 0.2s ease'
              }}
              className='edit-icon'
            />
          </div>
        )}

        {error && (
          <Text type='danger' style={{ fontSize: 12, marginTop: 8 }}>
            {error}
          </Text>
        )}
      </div>
    </>
  );
};
